import moment from 'moment-timezone';
import { call } from 'redux-saga/effects';
import { get, filter, includes, replace, startCase, slice } from 'lodash';
import crypto from 'crypto';
import unorm from 'unorm';
// using the /max instace to add landlines verification
import request from 'utils/request';

export function* sleep(time) {
  yield new Promise((resolve) => {
    setTimeout(resolve, time);
  });
}

export function parseDateFromNow(value) {
  if (!value) {
    return '';
  }
  return moment(value).fromNow();
}

export function phoneOnlyNumbers(value) {
  if (!value) {
    return value;
  }

  return value
    .replace('+1 ', '')
    .replace('-', '')
    .replace(/[^\d]/g, '');
}

function phoneFormat(value, format) {
  // The phone field on add new patients under waitlists accepts blank phone numbers, so this is a work around for it
  if (value) {
    // default, withoutDash, onlyDash
    const phoneValue = value.match(value.length === 10 ? /(\d{3})(\d{3})(\d{4})/ : /(\d{3})(\d{3})(\d{3})/);
    if (phoneValue) {
      if (format === 'withoutDash') {
        return `(${phoneValue[1]})${phoneValue[2]}${phoneValue[3]}`;
      }
      if (format === 'onlyDash') {
        return `${phoneValue[1]}-${phoneValue[2]}-${phoneValue[3]}`;
      }
      return `(${phoneValue[1]})${phoneValue[2]}-${phoneValue[3]}`;
    }
  }
  return '';
}

export function phoneMaskUS(value, format) {
  const onlyNums = value.replace('+1 ', '').replace(/\D/g, '');

  if (onlyNums) {
    return phoneFormat(onlyNums, format);
  }
}

export function* quiteRequest(endpoint) {
  try {
    const response = yield call(request, endpoint);

    return response && !response.isAppError ? response : undefined;
  } catch (err) {
    return undefined;
  }
}

export function formatPracticeLinkName(name) {
  return name
    ? name
        .toLowerCase()
        .replace(/\s/g, '-')
        .replace(/\W/g, '')
    : 'invalid';
}

export function generateHash(key) {
  return crypto
    .createHash('md5')
    .update(key)
    .digest('hex');
}

export function hasValidContactType(patient, settings) {
  const {
    cancellation: { protocol },
  } = settings;
  const contactsActive = filter(get(patient, 'contact'), 'active');
  const contactByVoiceOnly = contactsActive.length === 1 && contactsActive[0].type === 'voice';
  return !/parallel/.test(protocol) || !contactByVoiceOnly;
}

export function setTextAreaCaretPosition(input, position) {
  setTextAreaSelectionRange(input, position, position);
}

export function setTextAreaSelectionRange(input, selectionStart, selectionEnd) {
  if (input.setSelectionRange) {
    input.focus();
    input.setSelectionRange(selectionStart, selectionEnd);
  } else if (input.createTextRange) {
    const range = input.createTextRange();

    range.collapse(true);
    range.moveEnd('character', selectionEnd);
    range.moveStart('character', selectionStart);
    range.select();
  }
}

export function highlightText(text, typedWord) {
  if (text) {
    const wordLowerCase = startCase(typedWord.toLowerCase());
    const normalizedWord = unorm.nfd(wordLowerCase);
    const updatedWord = normalizedWord.replace(/([\u0300-\u036f]|[^0-9a-zA-Z\s])/g, '');

    const pattern = new RegExp(`(${updatedWord})`, 'gi');
    const newText = text.replace(pattern, `<strong>${updatedWord}</strong>`);
    return newText;
  }
}

export function highlightDOB(text, typedWord) {
  if (text && typedWord.length > 0) {
    if (includes(text, typedWord)) {
      const newText = replace(text, typedWord, typedWord.bold());
      return newText;
    }
  }
  return text;
}

export function highlightNumbers(text, typedWord) {
  if (text && typedWord.length > 0) {
    let cleanTypedWord1;
    let cleanTypedWord2;
    let cleanTypedWord3;
    let bold1;
    let bold2;
    let bold3;
    const cleanTypedWord = typedWord.replace(/[^A-Z0-9]+/gi, '');
    const cleanText = text.replace(/[()-]/g, '');
    const cleanText1 = slice(cleanText, 0, 3).join('');
    const cleanText2 = slice(cleanText, 3, 6).join('');
    const cleanText3 = slice(cleanText, 6, 10).join('');

    if (cleanTypedWord.length > 0) {
      cleanTypedWord1 = slice(cleanTypedWord, 0, 3).join('');
    }
    if (cleanTypedWord.length > 3) {
      cleanTypedWord2 = slice(cleanTypedWord, 3, 6).join('');
    }
    if (cleanTypedWord.length > 6) {
      cleanTypedWord3 = slice(cleanTypedWord, 6, 10).join('');
    }
    if (includes(cleanText1, cleanTypedWord1)) {
      bold1 = replace(cleanText1, cleanTypedWord1, cleanTypedWord1.bold());
    } else {
      bold1 = cleanText1;
    }
    if (includes(cleanText2, cleanTypedWord2)) {
      bold2 = replace(cleanText2, cleanTypedWord2, cleanTypedWord2.bold());
    } else {
      bold2 = cleanText2;
    }
    if (includes(cleanText3, cleanTypedWord3)) {
      bold3 = replace(cleanText3, cleanTypedWord3, cleanTypedWord3.bold());
    } else {
      bold3 = cleanText3;
    }
    return `(${bold1})${bold2}-${bold3}`;
  }
  return text;
}
